import React, { useEffect, useState } from 'react';
import {
  CustomButton,
  CustomInput,
  CustomTitle,
  ToggleSwitch,
} from '../../../components';
import { Checkbox, Col, Divider, Flex, Form, Input, message, Row } from 'antd';
import AlertSettings, {
  ALERT_SETTINGS_FORM_DEFAULTS,
  ALERT_SETTINGS_FORM_KEYS,
  alertSettingsToFormValues,
  alertSettingsToPayload,
  type AlertSettingsFormValues,
  type AlertSettingsResponse,
} from './AlertSettings';
import { GetCompnayInfo, SubmitCompnayInfo } from '../../../apis/settings';
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext';
import useUnauthorize from '../../../hooks/useUnauthorize';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useGateValue } from '@statsig/react-bindings';
import { COMPANY_INFO_QUERY_KEY } from '@/hooks/useManualShipmentCreationFlags';
import './CompanyInfo.css';

const DEFAULT_ADDRESS_MESSAGE =
  "We'll contact the courier to update the address";
const DEFAULT_TIME_MESSAGE =
  "We'll share that preference with the courier to see if it can be accommodated";

/** Nudge the checkbox square down a couple px so it optically sits with the title. */
const FEATURE_CHECKBOX_STYLES = {
  icon: { position: 'relative' as const, top: 2 },
};

interface GetCompanyInfoResponse {
  data: {
    data: {
      data: {
        name?: string;
        ai_company_name?: string;
        report_address_update_enabled?: boolean;
        report_delivery_time_enabled?: boolean;
        report_address_update_message?: string;
        report_delivery_time_message?: string;
        report_ask_address_change_scope?: boolean;
        show_emissions_estimate?: boolean;
        manual_shipment_track_enabled?: boolean;
        manual_shipment_quote_enabled?: boolean;
        manual_shipment_book_enabled?: boolean;
        manual_shipment_create_enabled?: boolean;
        alert_settings?: AlertSettingsResponse;
      };
      is_success?: boolean;
      message?: string;
    };
  };
}

interface FormValues extends AlertSettingsFormValues {
  ai_company_name: string;
  report_address_update_enabled: boolean;
  report_delivery_time_enabled: boolean;
  report_address_update_message: string;
  report_delivery_time_message: string;
  report_ask_address_change_scope: boolean;
  show_emissions_estimate: boolean;
  manual_shipment_track_enabled: boolean;
  manual_shipment_quote_enabled: boolean;
  manual_shipment_book_enabled: boolean;
  manual_shipment_create_enabled: boolean;
}

interface CompanyInfoProps {
  title?: string;
}

/**
 * Company information settings component for updating company details
 * and tracking-link report-issue configuration.
 */
const CompanyInfo: React.FC<CompanyInfoProps> = ({ title }) => {
  const [form] = Form.useForm<FormValues>();
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const { t } = useTranslation('settings');
  const { TextArea } = Input;
  const queryClient = useQueryClient();
  const alertAssignmentGateEnabled = useGateValue('alert_assignment');

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const addressEnabled = Form.useWatch('report_address_update_enabled', form);
  const timeEnabled = Form.useWatch('report_delivery_time_enabled', form);
  const emissionsEnabled = Form.useWatch('show_emissions_estimate', form);
  const trackEnabled = Form.useWatch('manual_shipment_track_enabled', form);
  const quoteEnabled = Form.useWatch('manual_shipment_quote_enabled', form);
  const bookEnabled = Form.useWatch('manual_shipment_book_enabled', form);
  const createEnabled = Form.useWatch('manual_shipment_create_enabled', form);

  useEffect(() => {
    showSpin();
    GetCompnayInfo(token ?? '')
      .then((res: GetCompanyInfoResponse) => {
        const data = res?.data?.data?.data;
        form.setFieldsValue({
          ai_company_name: data?.ai_company_name || data?.name || '',
          report_address_update_enabled:
            data?.report_address_update_enabled ?? true,
          report_delivery_time_enabled:
            data?.report_delivery_time_enabled ?? true,
          report_address_update_message:
            data?.report_address_update_message || DEFAULT_ADDRESS_MESSAGE,
          report_delivery_time_message:
            data?.report_delivery_time_message || DEFAULT_TIME_MESSAGE,
          report_ask_address_change_scope:
            data?.report_ask_address_change_scope ?? true,
          // The figure is already live in the drawer, so a company that has
          // never touched this setting keeps seeing it.
          show_emissions_estimate: data?.show_emissions_estimate ?? true,
          manual_shipment_track_enabled:
            data?.manual_shipment_track_enabled ?? true,
          manual_shipment_quote_enabled:
            data?.manual_shipment_quote_enabled ?? true,
          manual_shipment_book_enabled:
            data?.manual_shipment_book_enabled ?? true,
          manual_shipment_create_enabled:
            data?.manual_shipment_create_enabled ?? true,
          ...alertSettingsToFormValues(data?.alert_settings),
        });
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [form, hideSpin, showSpin, token, unauthorize]);

  const onFinish = (values: FormValues): void => {
    // onFinish only carries mounted fields, and the assignment list is not
    // mounted while owner assignment is off. Read the alert settings from the
    // full form store, otherwise saving would send that list as empty and the
    // backend would archive every assignment tag.
    const alertValues = form.getFieldsValue(true) as AlertSettingsFormValues;
    const companyValues = Object.fromEntries(
      Object.entries(values).filter(
        ([key]) => !ALERT_SETTINGS_FORM_KEYS.includes(key)
      )
    );
    handleSubmitCompanyInfo({
      ...companyValues,
      report_address_update_message:
        values.report_address_update_message?.trim() || DEFAULT_ADDRESS_MESSAGE,
      report_delivery_time_message:
        values.report_delivery_time_message?.trim() || DEFAULT_TIME_MESSAGE,
      ...(alertAssignmentGateEnabled
        ? { alert_settings: alertSettingsToPayload(alertValues) }
        : {}),
    });
  };

  const handleSubmitCompanyInfo = (model: object): void => {
    setIsFormValid(true);
    showSpin();
    SubmitCompnayInfo(token ?? '', model)
      .then((res: GetCompanyInfoResponse) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success('Settings updated successfully');
            // Show the saved tags with their real ids and usage counts.
            const saved = data?.data?.data?.alert_settings;
            if (alertAssignmentGateEnabled && saved) {
              form.setFieldsValue(alertSettingsToFormValues(saved));
            }
            const _ = queryClient.invalidateQueries({
              queryKey: COMPANY_INFO_QUERY_KEY,
            });
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  return (
    <div className='settings-content-wrapper'>
      <CustomTitle
        text={title}
        fontSize='20px'
        fontWeight='700'
        lineHeight='28px'
        color='var(--text-primary)'
        marginBottom='24px'
      />

      <Form
        form={form}
        name='ProfileForm'
        layout='vertical'
        colon={false}
        onFinish={onFinish}
        autoComplete='off'
        initialValues={{
          ai_company_name: '',
          report_address_update_enabled: true,
          report_delivery_time_enabled: true,
          report_address_update_message: DEFAULT_ADDRESS_MESSAGE,
          report_delivery_time_message: DEFAULT_TIME_MESSAGE,
          report_ask_address_change_scope: true,
          show_emissions_estimate: true,
          manual_shipment_track_enabled: true,
          manual_shipment_quote_enabled: true,
          manual_shipment_book_enabled: true,
          manual_shipment_create_enabled: true,
          ...ALERT_SETTINGS_FORM_DEFAULTS,
        }}
      >
        <Flex vertical gap={12}>
          <Row gutter={14}>
            <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={6}>
              <Form.Item
                className='custom-input form-input-style'
                label={t('name')}
                name='ai_company_name'
                rules={[
                  { required: true, message: t('pleaseEnterCompanyName') },
                  {
                    min: 2,
                    message: t('companyNameMinLength'),
                  },
                  {
                    max: 50,
                    message: t('companyNameMaxLength'),
                  },
                  {
                    pattern: /^[A-Za-z0-9\s'-]+$/,
                    message: t('pleaseEnterValidValue'),
                  },
                ]}
                style={{ marginBottom: '0px' }}
              >
                <CustomInput
                  placeholder={t('enterCompanyName')}
                  height='36px'
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
          </Row>
        </Flex>

        <Divider style={{ margin: '28px 0 24px' }} />

        <Flex vertical gap={28}>
          <div>
            <CustomTitle
              text='Tracking link configuration'
              fontSize='16px'
              fontWeight='600'
              lineHeight='20px'
              color='var(--text-primary)'
              marginTop='0'
              marginBottom='4px'
            />
            <CustomTitle
              text='Choose what recipients can report from their tracking link. Turn an option on to configure it.'
              fontSize='14px'
              fontWeight='400'
              lineHeight='20px'
              color='var(--text-secondary)'
              marginTop='0'
              marginBottom='0'
            />
          </div>

          <Flex vertical gap={12}>
            <Flex align='center' gap={12}>
              <ToggleSwitch
                alignLeft
                checked={!!addressEnabled}
                onChange={(e) =>
                  form.setFieldsValue({
                    report_address_update_enabled: e.target.checked,
                  })
                }
              />
              <Form.Item
                name='report_address_update_enabled'
                valuePropName='checked'
                hidden
              >
                <Checkbox />
              </Form.Item>
              <span className='company-info__section-title'>
                Update delivery address
              </span>
            </Flex>

            {addressEnabled ? (
              <Flex
                vertical
                gap={10}
                style={{
                  // Bar at toggle left; content sits under the toggle (not under the title)
                  marginLeft: 0,
                  paddingLeft: 14,
                  borderLeft: '2px solid #E8E8E8',
                  maxWidth: 480,
                }}
              >
                <Form.Item
                  name='report_address_update_message'
                  label={
                    <span className='company-info__label'>
                      Message shown to the recipient
                    </span>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={2}
                    style={{
                      background: 'var(--light-gray)',
                      border: '0px',
                      borderRadius: 8,
                      resize: 'none',
                      color: 'var(--text-secondary)',
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name='report_ask_address_change_scope'
                  valuePropName='checked'
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox>
                    <span className='company-info__label'>
                      Ask if the change is temporary or permanent
                    </span>
                  </Checkbox>
                </Form.Item>
              </Flex>
            ) : null}
          </Flex>

          <Flex vertical gap={12}>
            <Flex align='center' gap={12}>
              <ToggleSwitch
                alignLeft
                checked={!!timeEnabled}
                onChange={(e) =>
                  form.setFieldsValue({
                    report_delivery_time_enabled: e.target.checked,
                  })
                }
              />
              <Form.Item
                name='report_delivery_time_enabled'
                valuePropName='checked'
                hidden
              >
                <Checkbox />
              </Form.Item>
              <span className='company-info__section-title'>
                Suggest a different delivery time
              </span>
            </Flex>

            {timeEnabled ? (
              <Form.Item
                name='report_delivery_time_message'
                label={
                  <span className='company-info__label'>
                    Message shown to the recipient
                  </span>
                }
                style={{
                  marginBottom: 0,
                  marginLeft: 0,
                  paddingLeft: 14,
                  borderLeft: '2px solid #E8E8E8',
                  maxWidth: 480,
                }}
              >
                <TextArea
                  rows={2}
                  style={{
                    background: 'var(--light-gray)',
                    border: '0px',
                    borderRadius: 8,
                    resize: 'none',
                    color: 'var(--text-secondary)',
                  }}
                />
              </Form.Item>
            ) : null}
          </Flex>
        </Flex>

        <Divider style={{ margin: '28px 0 24px' }} />

        <Flex vertical gap={16}>
          <div>
            <CustomTitle
              text='Manual shipment creation'
              fontSize='16px'
              fontWeight='600'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='4px'
            />
            <CustomTitle
              text='Choose which shipment creation options appear for users in your company. Unchecked options are hidden everywhere.'
              fontSize='14px'
              fontWeight='400'
              lineHeight='20px'
              color='var(--text-secondary)'
              marginBottom='0'
            />
          </div>

          {(
            [
              {
                name: 'manual_shipment_track_enabled' as const,
                title: 'Track',
                description:
                  'Add an existing shipment by tracking number and follow it live.',
                checked: !!trackEnabled,
              },
              {
                name: 'manual_shipment_quote_enabled' as const,
                title: 'Quote',
                description:
                  'Request rates from connected transport providers before booking.',
                checked: !!quoteEnabled,
              },
              {
                name: 'manual_shipment_book_enabled' as const,
                title: 'Book',
                description: 'Quote and book directly with a provider.',
                checked: !!bookEnabled,
              },
              {
                name: 'manual_shipment_create_enabled' as const,
                title: 'Create',
                description:
                  'Build a shipment manually with full control over legs and sensors.',
                checked: !!createEnabled,
              },
            ] as const
          ).map((option) => (
            <Flex key={option.name} vertical gap={2}>
              {/* Match Emissions estimate: checkbox aligns to the title only;
                  description sits outside with left padding so antd doesn't
                  top-align the box against the full multi-line label. */}
              <Checkbox
                checked={option.checked}
                styles={FEATURE_CHECKBOX_STYLES}
                onChange={(e) =>
                  form.setFieldsValue({
                    [option.name]: e.target.checked,
                  })
                }
              >
                <span className='company-info__section-title'>
                  {option.title}
                </span>
              </Checkbox>
              <Form.Item
                name={option.name}
                valuePropName='checked'
                hidden
                style={{ marginBottom: 0 }}
              >
                <Checkbox />
              </Form.Item>
              <span className='company-info__option-description'>
                {option.description}
              </span>
            </Flex>
          ))}
        </Flex>

        {alertAssignmentGateEnabled && (
          <>
            <Divider style={{ margin: '28px 0 24px' }} />
            <AlertSettings />
          </>
        )}

        <Divider style={{ margin: '28px 0 24px' }} />

        <Flex vertical gap={24}>
          <div>
            <CustomTitle
              text='Shipment details'
              fontSize='16px'
              fontWeight='600'
              lineHeight='20px'
              color='var(--text-primary)'
              marginTop='0'
              marginBottom='4px'
            />
            {/* CustomTitle leaves marginTop undefined, so antd's own heading
                top margin opens a gap between a title and the line explaining
                it. Every title below the first pins it to zero. */}
            <CustomTitle
              text='Choose which fields appear on shipment pages for users in your company.'
              fontSize='14px'
              fontWeight='400'
              lineHeight='20px'
              color='var(--text-secondary)'
              marginTop='0'
              marginBottom='0'
            />
          </div>

          <Flex vertical gap={2}>
            {/* antd pads a Form.Item's control row out to `controlHeight`,
                which leaves the helper line floating below the label. Render
                the visible control outside the Form.Item and keep the field
                itself hidden (same pattern as Manual shipment creation). */}
            <Checkbox
              checked={!!emissionsEnabled}
              styles={FEATURE_CHECKBOX_STYLES}
              onChange={(e) =>
                form.setFieldsValue({
                  show_emissions_estimate: e.target.checked,
                })
              }
            >
              <span className='company-info__label'>
                Emissions estimate (CO₂e)
              </span>
            </Checkbox>
            <Form.Item
              name='show_emissions_estimate'
              valuePropName='checked'
              hidden
            >
              <Checkbox />
            </Form.Item>
            <CustomTitle
              text='Estimated emissions shown on each shipment page. Calculated per the GLEC Framework.'
              fontSize='14px'
              fontWeight='400'
              lineHeight='20px'
              color='var(--text-secondary)'
              marginTop='0'
              marginBottom='0'
              style={{ paddingLeft: 24 }}
            />
          </Flex>
        </Flex>

        <CustomButton
          text={t('save')}
          width='107px'
          height='36px'
          borderRadius='8px'
          bgColor='#000'
          color='#fff'
          border='0px'
          fontSize='14px'
          fontWeight='600'
          htmlType='submit'
          marginTop='36px'
          isFormValid={isFormValid}
        />
      </Form>
    </div>
  );
};

export default CompanyInfo;
